import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { 
  Loader2,
  Plus,
  Trash2,
  Save,
  Percent,
  Clock,
  Ruler,
  DollarSign
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const AdminCalculator = () => {
  const { api } = useAdmin();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.getCalculatorConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load config:', error);
      toast.error('Failed to load calculator configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateCalculatorConfig(config);
      toast.success('Calculator configuration saved');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateFrequency = (index, field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      newConfig.frequencies[index][field] = value;
      return newConfig;
    });
  };

  const updateSizeRange = (rangeIndex, frequency, field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      if (field === 'min_sqm' || field === 'max_sqm') {
        newConfig.size_ranges[rangeIndex][field] = parseInt(value) || 0;
      } else {
        newConfig.size_ranges[rangeIndex][frequency][field] = parseFloat(value) || 0;
      }
      return newConfig;
    });
  };

  const updateAddon = (index, field, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      if (field === 'price') {
        newConfig.addons[index][field] = parseFloat(value) || 0;
      } else {
        newConfig.addons[index][field] = value;
      }
      return newConfig;
    });
  };

  const addAddon = () => {
    setConfig(prev => ({
      ...prev,
      addons: [...prev.addons, { 
        id: `addon_${Date.now()}`, 
        label_sv: '', 
        label_en: '', 
        price: 0, 
        unit_sv: 'styck', 
        unit_en: 'each' 
      }]
    }));
  };

  const removeAddon = (index) => {
    setConfig(prev => ({
      ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Price Calculator</h1>
          <p className="text-gray-600">Configure time-based pricing and hourly rates</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save className="mr-2" size={18} />}
          Save Changes
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <Accordion type="multiple" defaultValue={['general', 'frequencies', 'size_ranges']} className="px-6">
          
          {/* General Settings */}
          <AccordionItem value="general">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Percent size={20} className="text-purple-600" />
                General Settings
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pb-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <Label className="text-lg font-medium">RUT Deduction</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      type="number"
                      value={(config.rut_deduction * 100).toFixed(0)}
                      onChange={(e) => setConfig(prev => ({ ...prev, rut_deduction: parseFloat(e.target.value) / 100 }))}
                      className="w-24"
                    />
                    <span className="text-gray-600">%</span>
                    <span className="text-sm text-gray-500">(Tax deduction for household services)</span>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Frequencies & Hourly Rates */}
          <AccordionItem value="frequencies">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-purple-600" />
                Frequencies & Hourly Rates
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pb-6">
                <p className="text-sm text-gray-500 mb-4">
                  Set the hourly rate (full price before RUT) for each cleaning frequency.
                </p>
                {config.frequencies?.map((freq, index) => (
                  <div key={freq.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-4 gap-4 flex-1">
                      <div>
                        <Label className="text-xs text-gray-500">ID</Label>
                        <Input
                          value={freq.id}
                          onChange={(e) => updateFrequency(index, 'id', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Swedish Label</Label>
                        <Input
                          value={freq.label_sv}
                          onChange={(e) => updateFrequency(index, 'label_sv', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">English Label</Label>
                        <Input
                          value={freq.label_en}
                          onChange={(e) => updateFrequency(index, 'label_en', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Hourly Rate (SEK)</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            value={freq.hourly_rate}
                            onChange={(e) => updateFrequency(index, 'hourly_rate', parseFloat(e.target.value) || 0)}
                          />
                          <span className="text-gray-500 text-sm whitespace-nowrap">kr/h</span>
                        </div>
                        <p className="text-xs text-orange-600 mt-1">
                          After RUT: {Math.round(freq.hourly_rate * (1 - config.rut_deduction))} kr/h
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Size Ranges & Time */}
          <AccordionItem value="size_ranges">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Ruler size={20} className="text-purple-600" />
                Size Ranges & Cleaning Time
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-6 pb-6">
                <p className="text-sm text-gray-500">
                  Configure base cleaning hours and additional time per m² for each size range and frequency.
                </p>
                
                {config.size_ranges?.map((range, rangeIndex) => (
                  <div key={rangeIndex} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-semibold">
                        {range.min_sqm}-{range.max_sqm} m²
                      </div>
                      <div className="flex items-center gap-2 flex-1">
                        <Label className="text-sm">Min:</Label>
                        <Input
                          type="number"
                          value={range.min_sqm}
                          onChange={(e) => updateSizeRange(rangeIndex, null, 'min_sqm', e.target.value)}
                          className="w-20"
                        />
                        <Label className="text-sm ml-4">Max:</Label>
                        <Input
                          type="number"
                          value={range.max_sqm}
                          onChange={(e) => updateSizeRange(rangeIndex, null, 'max_sqm', e.target.value)}
                          className="w-20"
                        />
                        <span className="text-gray-500">m²</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      {['weekly', 'biweekly', 'monthly'].map((freq) => (
                        <div key={freq} className="bg-gray-50 rounded-lg p-3">
                          <Label className="text-sm font-medium text-gray-700 capitalize mb-2 block">
                            {freq === 'weekly' ? 'Weekly' : freq === 'biweekly' ? 'Every 2 weeks' : 'Every 4 weeks'}
                          </Label>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500 w-20">Base hours:</Label>
                              <Input
                                type="number"
                                step="0.001"
                                value={range[freq]?.base_hours || 0}
                                onChange={(e) => updateSizeRange(rangeIndex, freq, 'base_hours', e.target.value)}
                                className="flex-1"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500 w-20">Hours/m²:</Label>
                              <Input
                                type="number"
                                step="0.0001"
                                value={range[freq]?.hours_per_sqm || 0}
                                onChange={(e) => updateSizeRange(rangeIndex, freq, 'hours_per_sqm', e.target.value)}
                                className="flex-1"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Addons */}
          <AccordionItem value="addons">
            <AccordionTrigger className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <DollarSign size={20} className="text-purple-600" />
                Add-on Services
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pb-6">
                {config.addons?.map((addon, index) => (
                  <div key={addon.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <Label className="text-xs text-gray-500">ID</Label>
                        <Input
                          value={addon.id}
                          onChange={(e) => updateAddon(index, 'id', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Swedish Label</Label>
                        <Input
                          value={addon.label_sv}
                          onChange={(e) => updateAddon(index, 'label_sv', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">English Label</Label>
                        <Input
                          value={addon.label_en}
                          onChange={(e) => updateAddon(index, 'label_en', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Price (SEK)</Label>
                        <Input
                          type="number"
                          value={addon.price}
                          onChange={(e) => updateAddon(index, 'price', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Unit (Swedish)</Label>
                        <Input
                          value={addon.unit_sv}
                          onChange={(e) => updateAddon(index, 'unit_sv', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Unit (English)</Label>
                        <Input
                          value={addon.unit_en}
                          onChange={(e) => updateAddon(index, 'unit_en', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          variant="outline" 
                          onClick={() => removeAddon(index)} 
                          className="text-red-600 hover:bg-red-50 w-full"
                        >
                          <Trash2 size={16} className="mr-2" /> Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={addAddon}
                  className="w-full"
                >
                  <Plus size={16} className="mr-2" /> Add Addon
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default AdminCalculator;
